import { useEffect, useMemo, useState } from 'react';

import { PaginatedQueryResult } from '@/reducers/usePaginatedQuery';

interface AllResultsPaginatedParams<TData, TMetadata> {
  paginatedQuery: PaginatedQueryResult<TData, TMetadata>;
  loadingStrategy?: 'waitForAllResults' | 'returnEachPage';
  enabled?: boolean;
}

export const useLoadAllPaginatedResults = <TData, TMetadata>({
  paginatedQuery,
  loadingStrategy = 'waitForAllResults',
  enabled = true,
}: AllResultsPaginatedParams<TData, TMetadata>): PaginatedQueryResult<
  TData,
  TMetadata
> => {
  const { pageInfo, fetchMore, fetching } = paginatedQuery;
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (pageInfo?.hasNextPage && pageInfo.endCursor && enabled) {
      fetchMore();
    } else if (!fetching) {
      setIsLoading(false);
    }
  }, [
    pageInfo?.hasNextPage,
    pageInfo?.endCursor,
    enabled,
    fetchMore,
    fetching,
    paginatedQuery,
  ]);

  return useMemo(() => {
    const fetching =
      enabled && (paginatedQuery.fetching || !!paginatedQuery.pageInfo?.hasNextPage);
    if (loadingStrategy === 'returnEachPage') {
      return { ...paginatedQuery, fetching: isLoading };
    }
    if (fetching) {
      return { ...paginatedQuery, fetching: isLoading, data: undefined };
    }
    return paginatedQuery;
  }, [enabled, loadingStrategy, paginatedQuery, isLoading]);
};
